import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { MaskContainer } from "by/gamefactory/cityversetycoon/scenes/common/components/MaskContainer";
import { IBuildingModel } from "by/gamefactory/cityversetycoon/scenes/common/models/Building/IBuildingModel";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { Container, Graphics, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { BuildingInfo } from "../../../mybuildings/components/BuildingInfo";
import { UnboardingResourcePackage } from "../../resources/UnboardingResourcePackage";

export class UnboardingBuildingContainer extends Container {
    private static readonly BUILDING_SIDE_SIZE = 820;
    private static readonly RECTANGLE_MASK_HEIGHT = 879;

    private readonly unboardingResources: UnboardingResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly myBuildingsDynamicResources: DynamicResourcesPackage;
    private readonly buildingModel: IBuildingModel;

    private buildingContainer: Container;
    private addressPlaceholder: Sprite;
    private address: Text;
    private locationPlaceholder: Graphics;
    private location: Text;
    private buildingInfo: BuildingInfo;

    private addressPlaceholderBackground: Graphics;
    private building: Sprite;
    private buildingMaskContainer: Container;
    private buildingMask: Graphics;
    private road: Sprite;

    public constructor(unboardingResources: UnboardingResourcePackage,
        commonResources: CommonResourcesPackage,
        myBuildingsDynamicResources: DynamicResourcesPackage,
        buildingModel: IBuildingModel
    ) {
        super();
        this.unboardingResources = unboardingResources;
        this.commonResources = commonResources;
        this.myBuildingsDynamicResources = myBuildingsDynamicResources;
        this.buildingModel = buildingModel;
        this.build();
        this.layout();
    }

    private build(): void {
        this.buildingContainer = new Container();

        this.building = new Sprite(this.myBuildingsDynamicResources.resource(this.buildingModel.imageUrl.value));
        this.building.width = this.building.height = UnboardingBuildingContainer.BUILDING_SIDE_SIZE;
        this.building.interactive = true;
        this.buildingMaskContainer = new MaskContainer();

        this.buildingMask = new Graphics();
        this.buildingMask.beginFill(0xffffff);
        this.buildingMask.drawRoundedRect(0, 0, 600, UnboardingBuildingContainer.RECTANGLE_MASK_HEIGHT, 22);
        this.buildingMask.endFill();
        this.buildingMaskContainer.addChild(this.buildingMask);
        this.buildingContainer.addChild(this.buildingMaskContainer);

        this.road = new Sprite(this.unboardingResources.assets_views_unboarding_building_road_png);
        this.road.width = 600;
        this.road.height = 312;

        this.buildingContainer.addChild(this.building);
        this.buildingContainer.addChild(this.road);

        this.buildingContainer.mask = this.buildingMaskContainer;

        this.addressPlaceholderBackground = new Graphics();
        this.addressPlaceholderBackground.beginFill(0xffffff);
        this.addressPlaceholderBackground.drawRect(0, 0, 236, 63);
        this.addressPlaceholderBackground.endFill();

        this.addressPlaceholder = new Sprite(this.unboardingResources.assets_views_unboarding_adress_placeholder_png);

        this.address = new Text(this.buildingModel.address.value, new StreetTextStyle());
        this.addressPlaceholder.addChild(this.address);

        this.addressPlaceholderBackground.addChild(this.addressPlaceholder);
        this.buildingContainer.addChild(this.addressPlaceholderBackground);

        this.locationPlaceholder = new Graphics();
        this.locationPlaceholder.beginFill(0x1F1F1F);
        this.locationPlaceholder.drawRect(0, 0, 236, 35);
        this.locationPlaceholder.endFill();

        this.location = new Text(`${this.buildingModel.zipCode.value}, ${this.buildingModel.city.value}, ${this.buildingModel.borough.value}`, new ZipTextStyle());
        this.locationPlaceholder.addChild(this.location);
        this.buildingContainer.addChild(this.locationPlaceholder);

        this.buildingInfo = new BuildingInfo(this.commonResources, this.buildingModel, this.buildingMask.width - 48);

        this.buildingContainer.addChild(this.buildingInfo);
        this.addChild(this.buildingContainer);
    }

    private layout(): void {
        this.buildingMask.position.set(this.building.width * 0.5 - this.buildingMask.width * 0.5, this.building.position.y + 30);
        this.road.position.set(this.building.width * 0.5 - this.road.width * 0.5, this.building.position.y + this.building.height);
        this.addressPlaceholder.position.set(0, this.addressPlaceholderBackground.height * 0.5 - this.addressPlaceholder.height * 0.5);
        this.addressPlaceholderBackground.position.set(this.buildingMask.position.x, this.buildingMask.position.y + 50);
        this.locationPlaceholder.position.set(this.buildingMask.position.x, this.addressPlaceholderBackground.position.y + this.addressPlaceholderBackground.height);
        this.buildingInfo.position.set(this.buildingMask.position.x + (this.buildingMask.width - this.buildingInfo.width) * 0.5, this.buildingMask.position.y + this.buildingMask.height - this.buildingInfo.height);
        this.adjustText();
        this.location.position.set(24, this.locationPlaceholder.height * 0.5 - this.location.height * 0.5);
        this.address.position.set(24, this.addressPlaceholder.height * 0.5 - this.address.height * 0.5 + 3);
    }

    private adjustText(): void {
        if (this.location.width + 24 >= this.locationPlaceholder.width * 0.9) {
            this.location.scale.set(this.locationPlaceholder.width * 0.85 / (this.location.width + 24));
        }

        if (this.address.width + 24 >= this.addressPlaceholder.width * 0.9) {
            this.address.scale.set(this.addressPlaceholder.width * 0.85 / (this.address.width + 24));
        }
    }
}

class StreetTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48
        }));
    }
}

class ZipTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#B9B2CC",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 24
        }));
    }
}